import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useListTrainingCoursesQuery,
  useGetTrainingCourseResultsQuery,
} from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  amber:     '#b06f00',
  red:       '#b00020',
};

const STATUS_STYLE = {
  not_started: { bg: 'rgba(88,88,91,0.15)',  color: brand.charcoal, label: 'Not started' },
  in_progress: { bg: 'rgba(176,111,0,0.15)', color: brand.amber,    label: 'In progress' },
  passed:      { bg: 'rgba(26,127,78,0.15)', color: brand.green,    label: 'Passed' },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.not_started;
  return (
    <span style={{
      background: s.bg, color: s.color,
      fontWeight: 700, fontSize: '0.7rem',
      textTransform: 'uppercase', letterSpacing: '0.5px',
      padding: '0.2rem 0.55rem', borderRadius: 999,
      display: 'inline-block',
    }}>{s.label}</span>
  );
}
StatusPill.propTypes = { status: PropTypes.string };

function ScoreCell({ score, threshold }) {
  if (score === null || score === undefined) return <span style={{ color: brand.charcoal }}>—</span>;
  const passed = threshold ? score >= threshold : true;
  return (
    <span style={{
      fontWeight: 700,
      color: passed ? brand.green : brand.red,
    }}>{score}%</span>
  );
}
ScoreCell.propTypes = { score: PropTypes.number, threshold: PropTypes.number };

function StatBox({ label, value, color }) {
  return (
    <div style={{ background: 'white', borderRadius: 10, padding: '0.85rem 1rem', boxShadow: '0 2px 8px rgba(10,41,71,0.06)', flex: '1 1 130px', minWidth: 0 }}>
      <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, fontWeight: 700, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: color || brand.deepBlue, lineHeight: 1 }}>{value}</div>
    </div>
  );
}
StatBox.propTypes = { label: PropTypes.string, value: PropTypes.node, color: PropTypes.string };

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function TrainingResults() {
  const isMobile = useIsMobile();
  const { data: courses = [], isLoading: coursesLoading } = useListTrainingCoursesQuery();
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Default to the first course as soon as the list arrives. Once the user
  // picks one explicitly we leave their selection alone.
  const effectiveId = selectedId ?? (courses[0]?.id ?? null);

  const { data: results, isLoading: resultsLoading } = useGetTrainingCourseResultsQuery(
    { id: effectiveId },
    { skip: !effectiveId }
  );

  const learners = results?.learners || [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return learners.filter(l => {
      if (statusFilter !== 'all' && l.status !== statusFilter) return false;
      if (!q) return true;
      const hay = [
        l.first_name, l.last_name, l.final_email, l.prior_email, l.username, l.title, l.firm,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [learners, search, statusFilter]);

  const passThreshold = results?.course?.pass_threshold;
  const summary = results?.summary;

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '1rem' : '1.5rem' }}>

      {/* Header banner */}
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: isMobile ? '1.25rem 1rem' : '1.5rem 1.75rem', marginBottom: isMobile ? '1rem' : '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '0.75rem' : '1rem', flexWrap: 'wrap' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: isMobile ? '0.5rem' : '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-clipboard-data" style={{ color: brand.goldenrod, fontSize: isMobile ? '1.4rem' : '1.75rem' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: isMobile ? '0.65rem' : '0.75rem', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 4px' }}>
              Compliance
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px', fontSize: isMobile ? '1.15rem' : '1.5rem', lineHeight: 1.2 }}>Training Results</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
              Who has completed each training and how they scored.
            </p>
          </div>
        </div>
      </div>

      {/* Course picker */}
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: isMobile ? '0.85rem' : '1rem 1.25rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.85rem', margin: 0 }}>Course</label>
        <select
          value={effectiveId || ''}
          onChange={(e) => setSelectedId(Number(e.target.value))}
          disabled={coursesLoading || courses.length === 0}
          style={{ flex: 1, minWidth: 200, border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.45rem 0.75rem', fontSize: '0.9rem', color: brand.deepBlue }}
        >
          {courses.length === 0 && <option value="">No courses available</option>}
          {courses.map(c => (
            <option key={c.id} value={c.id}>
              {c.title} {c.status === 'draft' ? ' (draft)' : c.status === 'archived' ? ' (archived)' : ''}
            </option>
          ))}
        </select>
        {results?.course?.pass_threshold !== undefined && (
          <span style={{ color: brand.charcoal, fontSize: '0.8rem', flexShrink: 0 }}>
            Pass threshold: <strong>{results.course.pass_threshold}%</strong>
          </span>
        )}
      </div>

      {!effectiveId ? (
        <div style={{ background: 'white', borderRadius: 12, padding: '3rem', textAlign: 'center', color: brand.charcoal }}>
          <i className="bi bi-clipboard-data" style={{ fontSize: '2.5rem', color: brand.softGray, display: 'block', marginBottom: '0.5rem' }} />
          <p style={{ margin: 0, fontWeight: 600 }}>Create and publish a course first to see results here.</p>
        </div>
      ) : resultsLoading || !results ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="spinner-border" style={{ color: brand.tealBlue }} />
        </div>
      ) : (
        <>
          {/* Summary stats */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
            <StatBox label="Total employees"     value={summary.total_employees} />
            <StatBox label="Passed"               value={summary.passed}        color={brand.green} />
            <StatBox label="In progress"          value={summary.in_progress}   color={brand.amber} />
            <StatBox label="Not started"          value={summary.not_started}   color={brand.charcoal} />
            <StatBox label="Avg best score"
                     value={summary.avg_best_score !== null ? `${summary.avg_best_score}%` : '—'} />
          </div>

          {/* Filter row */}
          <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '0.65rem 1rem', marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, title…"
              style={{ flex: 1, minWidth: 180, border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.4rem 0.75rem', fontSize: '0.85rem' }}
            />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.4rem 0.75rem', fontSize: '0.85rem', color: brand.deepBlue }}
            >
              <option value="all">All statuses</option>
              <option value="passed">Passed</option>
              <option value="in_progress">In progress</option>
              <option value="not_started">Not started</option>
            </select>
            <span style={{ color: brand.charcoal, fontSize: '0.75rem' }}>
              {filtered.length} of {learners.length}
            </span>
          </div>

          {/* Results table */}
          <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden' }}>
            {isMobile ? (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {filtered.map(l => (
                  <li key={l.directory_id} style={{ padding: '0.85rem 1rem', borderBottom: `1px solid ${brand.softGray}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: brand.deepBlue }}>{l.first_name} {l.last_name}</span>
                      <StatusPill status={l.status} />
                    </div>
                    <div style={{ fontSize: '0.78rem', color: brand.charcoal }}>{l.final_email}</div>
                    <div style={{ display: 'flex', gap: '0.85rem', marginTop: '0.35rem', fontSize: '0.78rem', color: brand.charcoal, flexWrap: 'wrap' }}>
                      <span>Best: <ScoreCell score={l.best_score} threshold={passThreshold} /></span>
                      <span>Attempts: <strong style={{ color: brand.deepBlue }}>{l.attempt_count || 0}</strong></span>
                      <span>Last: {fmtDate(l.latest_attempt)}</span>
                      <span>Completed: {fmtDate(l.last_completed_at)}</span>
                    </div>
                  </li>
                ))}
                {filtered.length === 0 && (
                  <li style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>No employees match.</li>
                )}
              </ul>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: brand.deepBlue, color: 'white' }}>
                    <th style={{ textAlign: 'left', padding: '0.6rem 0.85rem', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Employee</th>
                    <th style={{ textAlign: 'left', padding: '0.6rem 0.85rem', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '0.6rem 0.85rem', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Best score</th>
                    <th style={{ textAlign: 'right', padding: '0.6rem 0.85rem', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Attempts</th>
                    <th style={{ textAlign: 'left', padding: '0.6rem 0.85rem', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last attempt</th>
                    <th style={{ textAlign: 'left', padding: '0.6rem 0.85rem', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(l => (
                    <tr key={l.directory_id} style={{ borderBottom: `1px solid ${brand.softGray}` }}>
                      <td style={{ padding: '0.55rem 0.85rem' }}>
                        <div style={{ color: brand.deepBlue, fontWeight: 700, fontSize: '0.875rem' }}>{l.first_name} {l.last_name}</div>
                        <div style={{ color: brand.charcoal, fontSize: '0.75rem' }}>{l.final_email}</div>
                      </td>
                      <td style={{ padding: '0.55rem 0.85rem' }}>
                        <StatusPill status={l.status} />
                      </td>
                      <td style={{ padding: '0.55rem 0.85rem', textAlign: 'right' }}>
                        <ScoreCell score={l.best_score} threshold={passThreshold} />
                      </td>
                      <td style={{ padding: '0.55rem 0.85rem', textAlign: 'right', color: brand.deepBlue }}>{l.attempt_count || 0}</td>
                      <td style={{ padding: '0.55rem 0.85rem', color: brand.charcoal, fontSize: '0.85rem' }}>{fmtDate(l.latest_attempt)}</td>
                      <td style={{ padding: '0.55rem 0.85rem', color: brand.charcoal, fontSize: '0.85rem' }}>{fmtDate(l.last_completed_at)}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>No employees match.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
